import {useState, useEffect, useRef, Dispatch, SetStateAction} from 'react'
import {startOfDay, parseISO} from 'date-fns'
import {Space} from 'antd'

import styles from './CashflowForecast.module.scss'
import useStore from 'base/store'
import { PermissionType } from 'common/types'
import {useMount} from 'common/hooks'
import {Button, Paper, Loader, Filter} from 'common/components'
import {AntdDatePicker} from 'common/components/inputs/DatePicker'
import {
  formatDate,
  arrayFromMonthsCount,
  totalNumberFromArray,
  fixNumber,
  createReport,
} from 'common/helpers'
import {CASHFLOW_CHART_MONTHS_COUNT, MIN_PICKER_DATE} from 'common/constants'
import {
  CashflowPanel,
  CashflowForecastChart,
  CashflowForecastTable,
  CashflowForecastValue,
  useGetCashflowForecast,
} from '..'

interface ContentProps {
  cahsflowForecast: any
}

interface DateSelectProps {
  date: Date
  today: Date
  setDate: Dispatch<SetStateAction<Date>>
}

interface FiltersProps {
  isRequestsFilter: boolean
  cahsflowForecast: any
  selectedManagers: string[]
  selectedRequests: string[]
  setSelectedManagers: Dispatch<SetStateAction<string[]>>
  setSelectedRequests: Dispatch<SetStateAction<string[]>>
}

export default function CashflowForecast() {
  const role = useStore((state) => state.role)
  const todayRef = useRef(startOfDay(new Date()))
  const [date, setDate] = useState(todayRef.current)
  const [selectedManagers, setSelectedManagers] = useState<string[]>(
    role.manager ? null : []
  )
  const [selectedRequests, setSelectedRequests] = useState<string[]>(
    role.manager ? null : []
  )
  const {data: cahsflowForecast} = useGetCashflowForecast(
    formatDate(date, 'server')
  )
  const isMounted = useMount()
  const isRequestsFilter = !role.manager && date >= todayRef.current

  useEffect(() => {
    if (role.manager || !isMounted) return
    setSelectedManagers([])
    setSelectedRequests([])
  }, [cahsflowForecast]) // eslint-disable-line

  let filteredCashflowForecast = cahsflowForecast
    ? {
        ...cahsflowForecast.snapshot,
        projects: isRequestsFilter
          ? cahsflowForecast.snapshot.projects.filter(
              (project: any) => !isProjectWithActiveRequest(project)
            )
          : cahsflowForecast.snapshot.projects,
      }
    : null
  if (!role.manager && filteredCashflowForecast && selectedManagers.length) {
    filteredCashflowForecast = {
      ...filteredCashflowForecast,
      projects: filteredCashflowForecast.projects.filter((project: any) =>
        selectedManagers.includes(project.managerUuid)
      ),
    }
  }
  if (isRequestsFilter && filteredCashflowForecast && selectedRequests.length) {
    filteredCashflowForecast = {
      ...filteredCashflowForecast,
      projects: [
        ...filteredCashflowForecast.projects,
        ...cahsflowForecast.snapshot.projects.filter((project: any) =>
          selectedRequests.includes(project.uuid)
        ),
      ],
    }
  }

  const handleReport = () => {
    const data = getData(filteredCashflowForecast)
    createReport(
      getReportData(data),
      'Cashflow forecast',
      `cashflow-forecast-report_${formatDate(date, 'server')}`
    )
  }

  return (
    <Paper>
      <CashflowPanel
        margin="large"
        title="Cashflow forecast"
        reportDisabled={!cahsflowForecast}
        onReport={handleReport}
      >
        <DateSelect date={date} today={todayRef.current} setDate={setDate} />
        {!role.manager && (
          <Filters
            isRequestsFilter={isRequestsFilter}
            cahsflowForecast={cahsflowForecast}
            selectedManagers={selectedManagers}
            selectedRequests={selectedRequests}
            setSelectedManagers={setSelectedManagers}
            setSelectedRequests={setSelectedRequests}
          />
        )}
      </CashflowPanel>
      {filteredCashflowForecast ? (
        <Content cahsflowForecast={filteredCashflowForecast} />
      ) : (
        <Loader className={styles.loader} />
      )}
    </Paper>
  )
}

const Content = ({cahsflowForecast}: ContentProps) => {
  const permissions: PermissionType[] = useStore((state) => state.permissions)
  const data = getData(cahsflowForecast)

  return (
    !permissions.includes('view all cashflow data') ? null :
    <>
      <CashflowForecastChart cahsflowForecast={cahsflowForecast} data={data} />
      <CashflowForecastTable data={data.slice(1)} />
    </>
  )
}

const Filters = ({
  isRequestsFilter,
  cahsflowForecast,
  selectedManagers,
  selectedRequests,
  setSelectedManagers,
  setSelectedRequests,
}: FiltersProps) => {
  const managerItems = cahsflowForecast
    ? cahsflowForecast.snapshot.managers.map((manager: any) => ({
        value: manager.uuid,
        label: `${manager.firstName} ${manager.lastName}`,
      }))
    : []

  const requestItems = isRequestsFilter
    ? cahsflowForecast
      ? cahsflowForecast.snapshot.projects
          .filter((project: any) => isProjectWithActiveRequest(project))
          .map((project: any) => ({
            value: project.uuid,
            label: project.title,
          }))
      : []
    : undefined

  return (
    <Space size={12}>
      <Filter
        values={selectedManagers}
        allTitle="managers"
        label="manager"
        items={managerItems}
        renderButton={(label) => (
          <Button dropdown variant="secondary">
            {label === 'manager' ? 'all managers' : label}
          </Button>
        )}
        setValues={setSelectedManagers}
      />
      {isRequestsFilter && (
        <Filter
          values={selectedRequests}
          allTitle="requests excluded"
          label="request"
          items={requestItems}
          renderButton={(label) => (
            <Button dropdown variant="secondary">
              {label === 'request' ? 'all requests excluded' : label}
            </Button>
          )}
          disabled={!requestItems.length}
          setValues={setSelectedRequests}
        />
      )}
    </Space>
  )
}

const DateSelect = ({date, today, setDate}: DateSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const handleOpen = () => setIsOpen(true)

  const handleChange = (date: Date) => {
    setDate(date)
    setTimeout(() => setIsOpen(false), 0)
  }

  const disabledDate = (date: Date) => date < MIN_PICKER_DATE || date > today

  return (
    <Button dropdown variant="secondary" open={isOpen} onClick={handleOpen}>
      <span>by {formatDate(date)}</span>
      <AntdDatePicker
        inputReadOnly
        className={styles.datePicker}
        mode="date"
        showToday={false}
        allowClear={false}
        open={isOpen}
        value={date}
        disabledDate={disabledDate}
        onOpenChange={setIsOpen}
        onChange={handleChange}
      />
    </Button>
  )
}

const getData = (cahsflowForecast: any): CashflowForecastValue[] => {
  const initialData = arrayFromMonthsCount(
    CASHFLOW_CHART_MONTHS_COUNT,
    parseISO(cahsflowForecast.date)
  ).map((month, index) => ({
    monthDate: month,
    month: formatDate(month, 'serverSecond'),
    amount: index === 0 ? cahsflowForecast.openValue : 0,
    drawdownsAmount: 0,
    drawdownsCount: 0,
    repaymentsAmount: 0,
    repaymentsCount: 0,
    totalDiff: 0,
    diffType: null,
  }))
  const items = cahsflowForecast.projects.map(
    (project: any) => project.forecastItems
  )
  items.forEach((item: any) => {
    const itemEntries = Object.entries(item)
    itemEntries.forEach(([month, values]: any) => {
      if (initialData.some((initialItem) => initialItem.month === month)) {
        const current = initialData.find(
          (initialItem) => initialItem.month === month
        )
        current.drawdownsAmount += values.drawdown
          ? totalNumberFromArray(values.drawdown, 'amount')
          : 0
        current.drawdownsCount += values.drawdown ? values.drawdown.length : 0
        current.repaymentsAmount += values.repayment
          ? Math.abs(totalNumberFromArray(values.repayment, 'amount'))
          : 0
        current.repaymentsCount += values.repayment
          ? values.repayment.length
          : 0
      }
    })
  })
  let totalAmount = cahsflowForecast.openValue
  const data: CashflowForecastValue[] = initialData.map((item, index) => {
    const month = formatDate(item.monthDate, 'fourth')
    const totalDiff = item.drawdownsAmount - item.repaymentsAmount
    const prevAmount = totalAmount
    totalAmount =
      index === 0 ? -(totalAmount - totalDiff) : totalAmount + totalDiff
    return {
      ...item,
      month,
      totalDiff,
      empty: index === 0,
      amount: totalAmount,
      prevAmount: prevAmount,
      diffType:
        totalDiff === 0 ? null : totalDiff > 0 ? 'drawdown' : 'repayment',
    }
  })
  return data
}

const getReportData = (data: any[]) =>
  data.map((item) => ({
    Month: item.month,
    'Drawdowns count': item.drawdownsCount,
    'Drawdowns amount': fixNumber(item.drawdownsAmount),
    'Repayments count': item.repaymentsCount,
    'Repayments amount': fixNumber(item.repaymentsAmount),
    'Total amount': fixNumber(item.totalDiff),
    'Available fund': fixNumber(item.amount),
  }))

const completeRequestStatuses = ['opened', 'declined']

const isProjectWithActiveRequest = (project: any): boolean => {
  return project.loanRequests.some(
    (request: any) => !completeRequestStatuses.includes(request.status)
  )
}
